import { ModifierType } from '@sg/graphql-schema';

import {
  type ModifierItem,
  type PartialModifier,
  type ProductParam,
} from '../useLineItemModifierIngredients.types';
import { toIngredientKind } from './formatters';

// ─────────────────────────────────────────────────────────────────────────────

export function getDefaultDressingNames(product: ProductParam): Set<string> {
  const { ingredients = [] } = product;

  const dressingNames = ingredients
    .filter((ingredient) => ingredient.kind?.toLowerCase() === 'dressing')
    .map((ingredient) => ingredient.name?.toLowerCase() ?? '')
    .filter(Boolean);

  return new Set(dressingNames);
}

export function getSubbedIngredientNames(modifiers: ReadonlyArray<PartialModifier>): Set<string> {
  const names = modifiers
    .filter((modifier) => modifier.modifierType === ModifierType.Substitution)
    .map((modifier) => modifier.ingredient?.name?.toLowerCase() ?? '')
    .filter(Boolean);

  return new Set(names);
}

export function getAddedFromModifiers(
  modifiers: ReadonlyArray<PartialModifier>,
  defaultDressingNames: Set<string>,
): ModifierItem[] {
  return modifiers
    .filter(checkIfHasIngredient)
    .filter(
      (modifier) =>
        modifier.modifierType === ModifierType.Addition ||
        modifier.modifierType === ModifierType.Substitution ||
        modifier.modifierType === ModifierType.Included,
    )
    .filter((modifier) => !checkIfBreadModifier(modifier))
    .filter((modifier) => !checkIfDefaultDressingIncrease(modifier, defaultDressingNames))
    .map(toModifierItem);
}

export function getRemovedFromModifiers(
  modifiers: ReadonlyArray<PartialModifier>,
  subbedIngredientNames: Set<string>,
): ModifierItem[] {
  return modifiers
    .filter(checkIfHasIngredient)
    .filter((modifier) => modifier.modifierType === ModifierType.Removal)
    .filter((modifier) => {
      const name = modifier.ingredient?.name?.toLowerCase() ?? '';

      return !subbedIngredientNames.has(name);
    })
    .map(toModifierItem);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfHasIngredient(modifier: PartialModifier): boolean {
  return Boolean(modifier.ingredient?.name);
}

function toModifierItem(modifier: PartialModifier): ModifierItem {
  const ingredientName = modifier.ingredient?.name ?? '';
  const modifierCost = modifier.cost ?? 0;

  return {
    name: ingredientName.toLowerCase(),
    cost: modifierCost,
    kind: toIngredientKind(modifier.ingredient?.kind),
  };
}

function checkIfBreadModifier(modifier: PartialModifier): boolean {
  return modifier.ingredient?.name?.toLowerCase() === BREAD_INGREDIENT_NAME;
}

function checkIfDefaultDressingIncrease(
  modifier: PartialModifier,
  defaultDressingNames: Set<string>,
): boolean {
  const ingredientName = modifier.ingredient?.name?.toLowerCase() ?? '';

  return defaultDressingNames.has(ingredientName);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const BREAD_INGREDIENT_NAME = 'bread';
